import * as React from 'react';
import { faInfoCircle, faBackward, faStepBackward, faStepForward, faForward, faPause, faPlay, faUpRightAndDownLeftFromCenter, IconDefinition, faRectangleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TouchableOpacity, View, Switch } from 'react-native';
import MyAppText from './MyAppText';
import Tooltip from './Tooltip';
import { translate } from '../services/translate';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { faRectangleWide } from '@fortawesome/pro-solid-svg-icons';

const allowedPlaybackRates = [0.25, 0.5, 1.0, 2.0, 4.0, 8.0, 16.0];

type VideoPlayerControlsProps = {
    isLive: boolean;
    isPlaying: boolean;
    isTheaterMode: boolean;
    handleGoLive: () => void;
    onStepBack: (multiplier: number) => void;
    onStepForward: (multiplier: number) => void;
    onTogglePlay: () => void;
    onPressTheaterMode: () => void;
    onPressFullscreen: () => void;
    onPlaybackChange: (playbackRate: number) => void;
    hideFrameByFrame?: boolean;
    hideNavigationControls?: boolean;
    hideTheaterMode?: boolean;
    hideFullscreen?: boolean;
};

export function VideoPlayerControls({
    isLive,
    isPlaying,
    isTheaterMode,
    handleGoLive,
    onStepBack,
    onStepForward,
    onTogglePlay,
    onPressTheaterMode,
    onPressFullscreen,
    onPlaybackChange,
    hideFrameByFrame,
    hideNavigationControls,
    hideTheaterMode,
    hideFullscreen,
}: VideoPlayerControlsProps) {
    const { styles } = useStyles(stylesheet);

    const [playbackRate, setPlaybackRate] = React.useState(1.0);
    const isFrameByFrameAnalysis = playbackRate === 0.1;

    return (
        <View style={{
            backgroundColor: '#000028',
            padding: 5,
            paddingLeft: 15,
            paddingRight: 15,
            flexDirection: 'row',
            justifyContent: 'space-between',
        }}>
            <View style={{
                flex: 1,
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: 10
            }}>
                {!hideFrameByFrame && (
                    <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center', display: 'flex', flex: 1 }}>
                        <Tooltip style={{ width: 200, backgroundColor: 'white', color: 'black' }} tooltipDirection='top' tooltipContent={translate('frameByFrameAnalysis')}>
                            <FontAwesomeIcon icon={faInfoCircle} color='#fff'></FontAwesomeIcon>
                        </Tooltip>
                        <MyAppText style={styles.frameByFrameAnalysisText}>{translate('frameByFrameAnalysisTitle')}</MyAppText>
                        <Tooltip style={{ width: 140, backgroundColor: 'white', color: 'black', paddingLeft: 5, paddingRight: 5 }} tooltipContent={translate('frameByFrameSwitchTooltip')} tooltipDirection='top' disabled={!isLive}>
                            <Switch
                                trackColor={{ false: '#767577', true: '#767577' }}
                                thumbColor='#fff'
                                ios_backgroundColor="#767577"
                                style={isLive && { opacity: 0.3 }}
                                disabled={isLive}
                                onValueChange={() => {
                                    const newPlaybackRate = isFrameByFrameAnalysis ? 1.0 : 0.1;

                                    setPlaybackRate(newPlaybackRate);
                                    onPlaybackChange(newPlaybackRate);
                                }}
                                value={isFrameByFrameAnalysis}
                            />
                        </Tooltip>
                    </View>
                )}
                {!hideNavigationControls && (
                    <View style={{ flexDirection: 'row', gap: 10, flex: 1, justifyContent: 'center' }}>
                        <View style={{ flexDirection: 'row', gap: 15, display: 'flex', alignItems: 'center' }}>
                            <Tooltip style={{ width: 140, backgroundColor: 'white', color: 'black', paddingLeft: 5, paddingRight: 5 }} tooltipContent={translate('navigationControlsTooltip')} tooltipDirection='top' disabled={!isLive}>
                                <TouchableOpacity
                                    style={(isLive || playbackRate === 0.25 || isFrameByFrameAnalysis) && { opacity: 0.3 }}
                                    disabled={isLive || playbackRate === 0.25 || isFrameByFrameAnalysis}
                                    onPress={() => {
                                        const nextRate = allowedPlaybackRates.findLast(rate => rate < playbackRate);

                                        if (nextRate) {
                                            setPlaybackRate(nextRate);
                                            onPlaybackChange(nextRate);
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon icon={faBackward} color='#fff' />
                                </TouchableOpacity>
                            </Tooltip>
                            <Tooltip style={{ width: 140, backgroundColor: 'white', color: 'black', paddingLeft: 5, paddingRight: 5 }} tooltipContent={translate('navigationControlsTooltip')} tooltipDirection='top' disabled={!isLive}>
                                <TouchableOpacity style={isLive && { opacity: 0.3 }} disabled={isLive} onPress={() => onStepBack(playbackRate)}>
                                    <FontAwesomeIcon icon={faStepBackward} color='#fff'></FontAwesomeIcon>
                                </TouchableOpacity>
                            </Tooltip>
                            <MyAppText style={styles.playbackRateText}>{playbackRate}x</MyAppText>
                            <Tooltip style={{ width: 140, backgroundColor: 'white', color: 'black', paddingLeft: 5, paddingRight: 5 }} tooltipContent={translate('navigationControlsTooltip')} tooltipDirection='top' disabled={!isLive}>
                                <TouchableOpacity style={isLive && { opacity: 0.3 }} disabled={isLive} onPress={() => onStepForward(playbackRate)}>
                                    <FontAwesomeIcon icon={faStepForward} color='#fff'></FontAwesomeIcon>
                                </TouchableOpacity>
                            </Tooltip>
                            <Tooltip style={{ width: 140, backgroundColor: 'white', color: 'black', paddingLeft: 5, paddingRight: 5 }} tooltipContent={translate('navigationControlsTooltip')} tooltipDirection='top' disabled={!isLive}>
                                <TouchableOpacity
                                    style={(isLive || isFrameByFrameAnalysis || playbackRate === 16) && { opacity: 0.3 }}
                                    disabled={isLive || isFrameByFrameAnalysis || playbackRate === 16}
                                    onPress={() => {
                                        const nextRate = allowedPlaybackRates.find(rate => rate > playbackRate);

                                        if (nextRate) {
                                            setPlaybackRate(nextRate);
                                            onPlaybackChange(nextRate);
                                        }
                                    }}
                                >
                                    <FontAwesomeIcon icon={faForward} color='#fff'></FontAwesomeIcon>
                                </TouchableOpacity>
                            </Tooltip>
                        </View>
                    </View>
                )}
                <View style={{ flexDirection: 'row', gap: 15, flex: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                    {isLive ?
                        <MyAppText style={styles.liveButton}>{translate('live')}</MyAppText>
                        :
                        <TouchableOpacity onPress={() => {
                            setPlaybackRate(1.0);
                            handleGoLive();
                        }}>
                            <MyAppText style={styles.goLiveButton}>{translate('goLive')}</MyAppText>
                        </TouchableOpacity>
                    }
                    <TouchableOpacity onPress={onTogglePlay}>
                        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} color='#fff'></FontAwesomeIcon>
                    </TouchableOpacity>

                    {!hideTheaterMode && (
                        <TouchableOpacity onPress={onPressTheaterMode}>
                            <FontAwesomeIcon icon={isTheaterMode ? faRectangleXmark : faRectangleWide as IconDefinition} color='#fff'></FontAwesomeIcon>
                        </TouchableOpacity>
                    )}
                    {!hideFullscreen && (
                        <TouchableOpacity onPress={onPressFullscreen}>
                            <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} color='#fff'></FontAwesomeIcon>
                        </TouchableOpacity>
                    )}
                </View>
            </View>
        </View>
    );
}

const stylesheet = createStyleSheet(() => ({
    goLiveButton: {
        color: '#fff',
        backgroundColor: '#717171',
        borderRadius: 5,
        paddingHorizontal: 5
    },
    liveButton: {
        color: '#fff',
        backgroundColor: '#cf0000',
        borderRadius: 5,
        paddingHorizontal: 5
    },
    playbackRateText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: 'white',
        textShadowColor: 'black',
        textShadowRadius: 2,
        marginLeft: 10,
        marginRight: 10,
    },
    frameByFrameAnalysisText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: 'white',
        textShadowColor: 'black',
        textShadowRadius: 2,
    },
}));
